import { useState, useCallback, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { CHAT_FUNCTION } from '@/lib/chatFunction';

export interface Project {
  id: string;
  name: string;
  description?: string;
  privacy: string;
  webUrl?: string;
  createdAt: string;
  updatedAt?: string;
  chats: Array<{
    id: string;
    name?: string;
    webUrl: string;
    latestVersion?: {
      demoUrl?: string;
      screenshotUrl?: string;
    };
  }>;
}

export const useProjects = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [activeProjectId, setActiveProjectId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load projects on mount
  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase.functions.invoke(CHAT_FUNCTION, {
        body: { action: "listProjects" },
      });

      if (fetchError) {
        throw new Error(fetchError.message);
      }

      if (data.error) {
        throw new Error(data.error);
      }

      const loadedProjects = (data.projects || []).map((p: any) => ({
        id: p.id,
        name: p.name,
        description: p.description,
        privacy: p.privacy,
        webUrl: p.webUrl,
        createdAt: p.createdAt,
        updatedAt: p.updatedAt,
        chats: p.chats || [],
      }));

      setProjects(loadedProjects);
    } catch (err) {
      console.error("Failed to load projects:", err);
      setError(err instanceof Error ? err.message : "Failed to load projects");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const createProject = useCallback(async (name: string, description?: string, firstMessage?: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const { data, error: createError } = await supabase.functions.invoke(CHAT_FUNCTION, {
        body: {
          action: "createProject",
          projectName: name,
          projectDescription: description,
          firstMessage: firstMessage || name, // Fallback to name if firstMessage is not provided, though name is usually the prompt initially
        },
      });

      if (createError) {
        throw new Error(createError.message);
      }

      if (data.error) {
        throw new Error(data.error);
      }

      const newProject: Project = {
        id: data.id,
        name: data.name,
        description: data.description,
        privacy: data.privacy,
        webUrl: data.webUrl,
        createdAt: data.createdAt,
        updatedAt: data.updatedAt,
        chats: data.chats || [],
      };

      setProjects((prev) => [newProject, ...prev]);
      setActiveProjectId(newProject.id);

      return newProject;
    } catch (err) {
      console.error("Failed to create project:", err);
      setError(err instanceof Error ? err.message : "Failed to create project");
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updateProject = useCallback(async (projectId: string, updates: { name?: string; description?: string }) => {
    setError(null);

    try {
      const { data, error: updateError } = await supabase.functions.invoke(CHAT_FUNCTION, {
        body: {
          action: "updateProject",
          projectId,
          projectName: updates.name,
          projectDescription: updates.description,
        },
      });

      if (updateError) {
        throw new Error(updateError.message);
      }

      if (data.error) {
        throw new Error(data.error);
      }

      setProjects((prev) =>
        prev.map((p) =>
          p.id === projectId
            ? { ...p, name: data.name, description: data.description, updatedAt: data.updatedAt }
            : p
        )
      );

      return data;
    } catch (err) {
      console.error("Failed to update project:", err);
      setError(err instanceof Error ? err.message : "Failed to update project");
      return null;
    }
  }, []);

  const deleteProject = useCallback(async (projectId: string) => {
    setError(null);

    try {
      const { data, error: deleteError } = await supabase.functions.invoke(CHAT_FUNCTION, {
        body: {
          action: "deleteProject",
          projectId,
        },
      });

      if (deleteError) {
        throw new Error(deleteError.message);
      }

      if (data.error) {
        throw new Error(data.error);
      }

      setProjects((prev) => prev.filter((p) => p.id !== projectId));

      if (activeProjectId === projectId) {
        setActiveProjectId(null);
      }

      return true;
    } catch (err) {
      console.error("Failed to delete project:", err);
      setError(err instanceof Error ? err.message : "Failed to delete project");
      return false;
    }
  }, [activeProjectId]);

  const getProject = useCallback(async (projectId: string) => {
    try {
      const { data, error: fetchError } = await supabase.functions.invoke(CHAT_FUNCTION, {
        body: {
          action: "getProject",
          projectId,
        },
      });

      if (fetchError) {
        throw new Error(fetchError.message);
      }

      if (data.error) {
        throw new Error(data.error);
      }

      return data as Project;
    } catch (err) {
      console.error("Failed to get project:", err);
      return null;
    }
  }, []);

  // Fetch active project if not in list - runs when activeProjectId changes
  // Important: Do NOT wait for isLoading to complete, fetch immediately
  const [hasFetchedForId, setHasFetchedForId] = useState<string | null>(null);

  useEffect(() => {
    const fetchMissingProject = async () => {
      // Only fetch if we have an activeProjectId and haven't already fetched for this ID
      if (activeProjectId && activeProjectId !== hasFetchedForId) {
        setHasFetchedForId(activeProjectId); // Mark as fetching/fetched immediately

        // Check if already in projects list using callback pattern to get latest state
        setProjects(prev => {
          const found = prev.find(p => p.id === activeProjectId);
          if (!found) {
            // Not in list, fetch in background
            console.log('[useProjects] Active project not in list, fetching:', activeProjectId);
            getProject(activeProjectId).then(project => {
              if (project) {
                setProjects(prevProjects => {
                  // Avoid duplicates
                  if (prevProjects.some(p => p.id === project.id)) return prevProjects;
                  return [project, ...prevProjects];
                });
              }
            });
          }
          return prev; // Don't modify state in this callback
        });
      }
    };
    fetchMissingProject();
  }, [activeProjectId, getProject, hasFetchedForId]);

  const activeProject = projects.find((p) => p.id === activeProjectId) || null;

  return {
    projects,
    activeProject,
    activeProjectId,
    isLoading,
    error,
    setActiveProjectId,
    loadProjects,
    createProject,
    updateProject,
    deleteProject,
    getProject,
  };
};
